import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  X, Save, Loader2, CheckCircle, ChevronRight, ChevronLeft, 
  Search, MessageSquare, Info, AlertCircle, ClipboardCheck,
  ClipboardList,
  Layout, Eye, ArrowLeft, ArrowRight, ShieldCheck, Tag,
  StickyNote, CheckSquare, Type, Hash, ArrowUpRight,
  Plus, List, BookOpen, Edit3, Trash2, Pin, Layers,
  ChevronDown, BookMarked, History, ImageIcon,
  ExternalLink, PanelLeftClose, PanelLeft, Camera,
  Image as LucideImage,
  Check, Home, Building, Zap, Droplets, Wind, Thermometer,
  Shield, Construction, Wrench, Hammer, Lightbulb, Power,
  Waves, Flame, Bug, Box, Scaling, MapPin,
  ArrowLeftCircle, ArrowRightCircle, Maximize2,
  Palette, AlignLeft, Menu
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Inspection, Location, ReportTemplate, InspectionReport, ReportSection, ReportItem, ReportElement, InspectionNarrative, ReportFinding, NarrativeTag } from '../types';
import { supabase, toSnakeCase, getErrorMessage, toCamelCase } from '../lib/supabase';
import ImageAnnotator from './ImageAnnotator';

// Map icon names to components for rendering
const ICON_MAP: Record<string, any> = {
  'Home': Home,
  'Building': Building,
  'Zap': Zap,
  'Droplets': Droplets,
  'Wind': Wind,
  'Thermometer': Thermometer,
  'Shield': Shield,
  'Construction': Construction,
  'Wrench': Wrench,
  'Hammer': Hammer,
  'Search': Search,
  'Lightbulb': Lightbulb,
  'Power': Power,
  'Waves': Waves,
  'Flame': Flame,
  'Bug': Bug,
  'Camera': Camera,
  'Box': Box,
  'Scaling': Scaling,
  'MapPin': MapPin,
  'Layout': Layout,
  'Layers': Layers,
  'Info': Info,
  'ClipboardList': ClipboardList
};

const DynamicIcon = ({ name, size = 16, className = "" }: { name?: string, size?: number, className?: string }) => {
  const IconComponent = name && ICON_MAP[name] ? ICON_MAP[name] : (ICON_MAP['Info']);
  return <IconComponent size={size} className={className} />;
};

interface ReportEditorProps {
  inspection: Inspection;
  report: InspectionReport;
  property: Location;
  templates: ReportTemplate[];
  reports: InspectionReport[];
  setReports: React.Dispatch<React.SetStateAction<InspectionReport[]>>;
  narratives: InspectionNarrative[];
  tags: NarrativeTag[];
  onClose: () => void;
}

const ReportEditor: React.FC<ReportEditorProps> = ({ 
  inspection, report: currentReport, property, templates, reports, setReports, narratives, tags, onClose 
}) => {
  const [activeSectionIdx, setActiveSectionIdx] = useState(0);
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCatalog, setShowCatalog] = useState<{ itemId: string } | null>(null);
  const [narSearchQuery, setNarSearchQuery] = useState('');
  const [editingFinding, setEditingFinding] = useState<{ itemId: string, findingId: string, title: string, content: string } | null>(null);
  const [uploadingFindingPhoto, setUploadingFindingPhoto] = useState<string | null>(null);
  const [lightboxImage, setLightboxImage] = useState<{ url: string, description: string } | null>(null);
  const [annotatingImage, setAnnotatingImage] = useState<{ itemId: string, findingId: string, url: string, index: number, isRegistry: boolean } | null>(null);
  const findingPhotoInputRef = useRef<HTMLInputElement>(null);

  // Unified Structure Discovery
  const structure = useMemo(() => {
    if (!currentReport) return [];
    const answers = currentReport.answers || {};
    const discoverySource = 
      answers.structure || 
      answers._structure || 
      currentReport.snapshotStructure || 
      (currentReport as any).snapshot_structure;

    if (discoverySource && Array.isArray(discoverySource)) {
      return toCamelCase(discoverySource) as ReportSection[];
    }
    const sourceTemplate = templates.find(t => t.id === currentReport.templateId);
    return sourceTemplate?.structure ? (toCamelCase(sourceTemplate.structure) as ReportSection[]) : [];
  }, [currentReport, templates]);

  useEffect(() => {
    if (structure.length > 0 && !activeItemId) {
      setActiveItemId(structure[activeSectionIdx].items[0]?.id || null);
    }
  }, [activeSectionIdx, structure]);

  // Helper to ensure images are always handled as objects { url, description }
  const hydrateImages = (images: any[]): { url: string, description: string }[] => {
    if (!images) return [];
    return images.map(img => {
      if (typeof img === 'string') return { url: img, description: '' };
      return { url: img.url || '', description: img.description || '' };
    });
  };

  const addFinding = async (itemId: string, nar: Partial<InspectionNarrative>) => {
    if (!currentReport) return;
    const currentFindingsMap = currentReport.answers.findings || {};
    const itemFindings = currentFindingsMap[itemId] || [];
    if (nar.id && itemFindings.some(f => f.sourceId === nar.id)) return;

    const newFinding: any = {
      id: crypto.randomUUID(),
      sourceId: nar.id,
      title: nar.title || 'New Observation',
      content: nar.content || '',
      registryImages: (nar.images || []).map(url => ({ url, description: '' })),
      findingImages: [],
      tagIds: nar.tagIds || []
    };

    const newFindingsMap = { ...currentFindingsMap, [itemId]: [...itemFindings, newFinding] };
    const newAnswers = { ...currentReport.answers, findings: newFindingsMap, structure: structure };

    setReports(prev => prev.map(r => r.id === currentReport.id ? { ...r, answers: newAnswers } : r));
    try { await supabase.from('inspection_reports').update({ answers: newAnswers }).eq('id', currentReport.id); } catch (err) { console.error("Finding sync failed:", err); }
    setShowCatalog(null);
  };

  const updateAnswer = async (elementId: string, value: any) => {
    if (!currentReport) return;
    const newAnswers = { ...currentReport.answers, [elementId]: value, structure: structure };
    setReports(prev => prev.map(r => r.id === currentReport.id ? { ...r, answers: newAnswers } : r));
    const currentItem = structure.flatMap(s => s.items).find(i => i.elements.some(e => e.id === elementId));
    if (currentItem) {
      const element = currentItem.elements.find(e => e.id === elementId);
      if (element && element.optionNarratives) {
        const selectedOptions = Array.isArray(value) ? value : [value];
        selectedOptions.forEach(val => {
           const narId = element.optionNarratives![val];
           if (narId) {
             const nar = narratives.find(n => n.id === narId);
             if (nar) addFinding(currentItem.id, nar);
           }
        });
      }
    }
    try { await supabase.from('inspection_reports').update({ answers: newAnswers }).eq('id', currentReport.id); } catch (err) { console.error("Answer sync failed:", err); }
  };

  const updateFinding = async (itemId: string, findingId: string, updates: Partial<ReportFinding>) => {
    if (!currentReport) return;
    const currentFindingsMap = currentReport.answers.findings || {};
    const itemFindings = currentFindingsMap[itemId] || [];
    const newFindingsMap = { ...currentFindingsMap, [itemId]: itemFindings.map(f => f.id === findingId ? { ...f, ...updates } : f) };
    const newAnswers = { ...currentReport.answers, findings: newFindingsMap, structure: structure };
    setReports(prev => prev.map(r => r.id === currentReport.id ? { ...r, answers: newAnswers } : r));
    try { await supabase.from('inspection_reports').update({ answers: newAnswers }).eq('id', currentReport.id); } catch (err) { console.error("Finding update failed"); }
    setEditingFinding(null);
  };

  const handleUpdateDescriptionState = (itemId: string, findingId: string, imageIndex: number, description: string, isRegistry: boolean) => {
    if (!currentReport) return;
    const currentFindingsMap = currentReport.answers.findings || {};
    const itemFindings = currentFindingsMap[itemId] || [];
    const targetKey = isRegistry ? 'registryImages' : 'findingImages';
    const newFindingsMap = { 
      ...currentFindingsMap, 
      [itemId]: itemFindings.map(f => {
        if (f.id !== findingId) return f;
        const images = hydrateImages((f as any)[targetKey] || []);
        if (images[imageIndex]) images[imageIndex].description = description;
        return { ...f, [targetKey]: images };
      })
    };
    const newAnswers = { ...currentReport.answers, findings: newFindingsMap, structure: structure };
    setReports(prev => prev.map(r => r.id === currentReport.id ? { ...r, answers: newAnswers } : r));
  };

  const updateFindingImageDescription = async (itemId: string, findingId: string, imageIndex: number, description: string, isRegistry: boolean) => {
    if (!currentReport) return;
    try { await supabase.from('inspection_reports').update({ answers: currentReport.answers }).eq('id', currentReport.id); } catch (err) { console.error("Image description sync failed", err); }
  };

  const moveFindingImage = async (itemId: string, findingId: string, imageIndex: number, direction: 'left' | 'right') => {
    if (!currentReport) return;
    const currentFindingsMap = currentReport.answers.findings || {};
    const itemFindings = currentFindingsMap[itemId] || [];
    const finding = itemFindings.find(f => f.id === findingId);
    if (!finding) return;
    const images = hydrateImages((finding as any).findingImages || []);
    const targetIndex = direction === 'left' ? imageIndex - 1 : imageIndex + 1;
    if (targetIndex >= 0 && targetIndex < images.length) {
      [images[imageIndex], images[targetIndex]] = [images[targetIndex], images[imageIndex]];
      const newFindingsMap = { ...currentFindingsMap, [itemId]: itemFindings.map(f => f.id === findingId ? { ...f, findingImages: images } : f) };
      const newAnswers = { ...currentReport.answers, findings: newFindingsMap, structure: structure };
      setReports(prev => prev.map(r => r.id === currentReport.id ? { ...r, answers: newAnswers } : r));
      try { await supabase.from('inspection_reports').update({ answers: newAnswers }).eq('id', currentReport.id); } catch (err) { console.error("Finding image reorder failed"); }
    }
  };

  const deleteFindingImage = async (itemId: string, findingId: string, imageIndex: number) => {
    if (!currentReport || !confirm('Permanently remove this photo from the finding?')) return;
    const currentFindingsMap = currentReport.answers.findings || {};
    const itemFindings = currentFindingsMap[itemId] || [];
    const finding = itemFindings.find(f => f.id === findingId);
    if (!finding) return;
    const images = hydrateImages((finding as any).findingImages || []);
    const newImages = images.filter((_, i) => i !== imageIndex);
    const newFindingsMap = { ...currentFindingsMap, [itemId]: itemFindings.map(f => f.id === findingId ? { ...f, findingImages: newImages } : f) };
    const newAnswers = { ...currentReport.answers, findings: newFindingsMap, structure: structure };
    setReports(prev => prev.map(r => r.id === currentReport.id ? { ...r, answers: newAnswers } : r));
    try { await supabase.from('inspection_reports').update({ answers: newAnswers }).eq('id', currentReport.id); } catch (err) { console.error("Finding image delete failed"); }
  };

  const deleteFinding = async (itemId: string, findingId: string) => {
    if (!currentReport || !confirm('Purge finding?')) return;
    const currentFindingsMap = currentReport.answers.findings || {};
    const itemFindings = currentFindingsMap[itemId] || [];
    const newFindingsMap = { ...currentFindingsMap, [itemId]: itemFindings.filter(f => f.id !== findingId) };
    const newAnswers = { ...currentReport.answers, findings: newFindingsMap, structure: structure };
    setReports(prev => prev.map(r => r.id === currentReport.id ? { ...r, answers: newAnswers } : r));
    try { await supabase.from('inspection_reports').update({ answers: newAnswers }).eq('id', currentReport.id); } catch (err) { console.error("Finding deletion failed"); }
  };

  const handleUploadFindingPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingFindingPhoto || !currentReport) return;
    const [itemId, findingId] = uploadingFindingPhoto.split(':');
    const findingMap = currentReport.answers.findings || {};
    const itemFindings = findingMap[itemId] || [];
    setSaving(true);
    try {
      const fileName = `findings/${currentReport.id}/${findingId}_${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
      const { error: uploadError } = await supabase.storage.from('inspection-photos').upload(fileName, file);
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('inspection-photos').getPublicUrl(fileName);
      const newFindings = itemFindings.map((f: any) => {
        if (f.id === findingId) {
          const currentPhotos = hydrateImages(f.findingImages || []);
          return { ...f, findingImages: [...currentPhotos, { url: publicUrl, description: '' }] };
        }
        return f;
      });
      const newFindingsMap = { ...findingMap, [itemId]: newFindings };
      const newAnswers = { ...currentReport.answers, findings: newFindingsMap, structure: structure };
      setReports(prev => prev.map(r => r.id === currentReport.id ? { ...r, answers: newAnswers } : r));
      await supabase.from('inspection_reports').update({ answers: newAnswers }).eq('id', currentReport.id);
    } catch (err) { alert(`Photo upload failure: ${getErrorMessage(err)}`); } finally { setSaving(false); setUploadingFindingPhoto(null); }
  };

  const handleSaveAnnotatedImage = async (base64Data: string) => {
    if (!annotatingImage || !currentReport) return;
    const { itemId, findingId, index, isRegistry } = annotatingImage;
    setSaving(true);
    try {
      const res = await fetch(base64Data);
      const blob = await res.blob();
      const file = new File([blob], `markup_${Date.now()}.jpg`, { type: 'image/jpeg' });
      const fileName = `findings/${currentReport.id}/${findingId}_annotated_${Date.now()}.jpg`;
      const { error: uploadError } = await supabase.storage.from('inspection-photos').upload(fileName, file);
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('inspection-photos').getPublicUrl(fileName);
      const currentFindingsMap = currentReport.answers.findings || {};
      const itemFindings = currentFindingsMap[itemId] || [];
      const newFindingsMap = { 
        ...currentFindingsMap, 
        [itemId]: itemFindings.map(f => {
          if (f.id !== findingId) return f;
          if (isRegistry) {
            const nextReg = hydrateImages((f as any).registryImages || []);
            if (nextReg[index]) nextReg[index].url = publicUrl;
            return { ...f, registryImages: nextReg };
          } else {
            const nextFind = hydrateImages((f as any).findingImages || []);
            if (nextFind[index]) nextFind[index].url = publicUrl;
            return { ...f, findingImages: nextFind };
          }
        })
      };
      const newAnswers = { ...currentReport.answers, findings: newFindingsMap, structure: structure };
      setReports(prev => prev.map(r => r.id === currentReport.id ? { ...r, answers: newAnswers } : r));
      await supabase.from('inspection_reports').update({ answers: newAnswers }).eq('id', currentReport.id);
      setAnnotatingImage(null);
      alert("Technical markup archived.");
    } catch (err) { alert("Archival failure: " + getErrorMessage(err)); } finally { setSaving(false); }
  };

  const completionStats = useMemo(() => {
    if (!currentReport || structure.length === 0) return { percent: 0, missing: 0 };
    let totalReq = 0, filledReq = 0;
    structure.forEach(s => s.items.forEach(i => i.elements.forEach(e => {
       if (e.required) {
          totalReq++;
          const val = currentReport.answers[e.id];
          if (val !== undefined && val !== '' && val !== false && (!Array.isArray(val) || val.length > 0)) filledReq++;
       }
    })));
    return { percent: totalReq === 0 ? 100 : Math.round((filledReq / totalReq) * 100), missing: totalReq - filledReq };
  }, [structure, currentReport]);

  if (!currentReport || structure.length === 0) return (
     <div className="h-screen bg-[#f8faf7] flex flex-col items-center justify-center p-8 space-y-6">
        <Loader2 className="animate-spin text-[#606c38]" size={48} />
        <button onClick={onClose} className="px-6 py-2 bg-slate-900 text-white rounded-lg text-xs font-black uppercase tracking-widest hover:bg-slate-800 transition-all">Abort Sync</button>
     </div>
  );

  const currentSection = structure[activeSectionIdx];
  const currentItem = currentSection?.items.find(i => i.id === activeItemId) || currentSection?.items[0];

  return (
    <div className="fixed inset-0 z-[500] bg-[#f8faf7] flex flex-col animate-in fade-in duration-500 overflow-hidden">
      <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-4 flex items-center justify-between shrink-0 z-[100] shadow-sm h-16 sm:h-20">
         <div className="flex items-center gap-3 sm:gap-4">
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 transition-all"><ArrowLeft size={20} /></button>
            <div className="min-w-0">
               <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-[10px] sm:text-xs font-black text-slate-900 uppercase tracking-tighter truncate leading-none">Technical Audit</h2>
                  <div className="lg:hidden flex items-center gap-1 bg-slate-50 px-2 py-0.5 rounded border border-slate-100">
                    <span className="text-[8px] font-black text-[#606c38] uppercase truncate max-w-[60px]">{currentSection?.name}</span>
                    <ChevronRight size={8} className="text-slate-300" />
                    <span className="text-[8px] font-black text-slate-400 uppercase truncate max-w-[60px]">{currentItem?.name}</span>
                  </div>
               </div>
               <p className="text-[9px] sm:text-[10px] text-slate-400 font-bold uppercase tracking-widest truncate">{property.address}</p>
            </div>
         </div>
         <div className="flex items-center gap-4 sm:gap-8">
            <div className="hidden md:flex flex-col items-end gap-1">
               <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest leading-none">{completionStats.percent}% Flow Complete</span>
               <div className="h-1 w-32 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-[#606c38] transition-all duration-700" style={{ width: `${completionStats.percent}%` }}></div></div>
            </div>
            <button onClick={() => setMobileNavOpen(true)} className="lg:hidden p-2 bg-[#606c38] text-white rounded-lg shadow-lg active:scale-95 transition-all"><Menu size={20} /></button>
            <button onClick={onClose} className="hidden sm:block px-5 py-2.5 bg-slate-900 text-white rounded-lg text-xs font-black uppercase tracking-widest hover:bg-slate-800 shadow-lg transition-all">Save & Exit</button>
         </div>
      </header>

      <div className="flex-1 flex overflow-hidden h-full relative">
         {/* Desktop Pane 1: Flow Sections */}
         <aside className={`${sidebarCollapsed ? 'w-16' : 'w-64'} hidden lg:flex bg-white border-r border-slate-200 flex flex-col shrink-0 transition-all duration-300 z-[90] h-full`}>
            <div className="p-4 border-b border-slate-100 flex items-center justify-between h-14">
               {!sidebarCollapsed && <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Global Protocol</span>}
               <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-300">
                  {sidebarCollapsed ? <PanelLeft size={16} /> : <PanelLeftClose size={16} />}
               </button>
            </div>
            <nav className="flex-1 overflow-y-auto no-scrollbar p-2 space-y-1">
               {structure.map((section, idx) => (
                 <button 
                  key={section.id} 
                  onClick={() => { setActiveSectionIdx(idx); setActiveItemId(section.items[0]?.id || null); }} 
                  title={section.name}
                  className={`w-full text-left p-4 rounded-xl transition-all flex items-center gap-4 ${activeSectionIdx === idx ? 'bg-[#606c38] text-white shadow-lg' : 'text-slate-400 hover:bg-slate-50'}`}
                >
                    <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-black/5 flex items-center justify-center font-black text-[10px]">
                       <DynamicIcon name={section.icon} size={14} className={activeSectionIdx === idx ? 'text-white' : 'text-slate-400'} />
                    </div>
                    {!sidebarCollapsed && <span className="text-[11px] font-black uppercase tracking-tight truncate">{section.name}</span>}
                 </button>
               ))}
            </nav>
         </aside>

         {/* Desktop Pane 2: Area Registry */}
         <aside className="w-72 hidden lg:flex bg-slate-50/50 border-r border-slate-200 flex flex-col shrink-0 z-[80] h-full">
            <div className="p-5 border-b border-slate-200 flex items-center gap-2 h-14">
               <Layers size={14} className="text-slate-400" />
               <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Area Registry</span>
            </div>
            <nav className="flex-1 overflow-y-auto no-scrollbar p-3 space-y-1.5">
               {currentSection?.items.map((item) => {
                  const hasData = item.elements.some(e => currentReport.answers[e.id]);
                  return (
                    <button 
                      key={item.id} 
                      onClick={() => setActiveItemId(item.id)}
                      className={`w-full text-left p-4 rounded-xl border transition-all flex items-center justify-between group ${activeItemId === item.id ? 'bg-white border-2 border-[#606c38] text-[#606c38] shadow-sm' : 'bg-white/40 border-slate-100 text-slate-500 hover:bg-white'}`}
                    >
                        <div className="min-w-0 flex-1 flex items-center gap-3">
                           <DynamicIcon name={item.icon} size={14} className={activeItemId === item.id ? 'text-[#606c38]' : 'text-slate-300'} />
                           <span className="text-[11px] font-bold truncate uppercase tracking-tight">{item.name}</span>
                        </div>
                        <div className={`w-1.5 h-1.5 rounded-full ${hasData ? 'bg-emerald-500' : 'bg-slate-200'}`} />
                    </button>
                  );
               })}
            </nav>
         </aside>

         {/* Mobile Navigation Hub Drawer */}
         {mobileNavOpen && (
            <div className="fixed inset-0 z-[1000] flex">
               <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={() => setMobileNavOpen(false)} />
               <div className="bg-white w-[85vw] max-w-sm h-full relative shadow-2xl flex flex-col animate-in slide-in-from-right-full duration-300">
                  <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                    <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest">Forensic Navigator</h3>
                    <button onClick={() => setMobileNavOpen(false)} className="p-2 text-slate-400 hover:text-slate-600"><X size={20} /></button>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-8 no-scrollbar">
                     <div className="space-y-3">
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">Audit Section</label>
                        <div className="grid grid-cols-1 gap-2">
                           {structure.map((section, idx) => (
                             <button 
                                key={section.id} 
                                onClick={() => { setActiveSectionIdx(idx); setActiveItemId(section.items[0]?.id || null); }}
                                className={`w-full text-left p-4 rounded-2xl transition-all flex items-center gap-4 ${activeSectionIdx === idx ? 'bg-[#606c38] text-white shadow-xl' : 'bg-slate-50 text-slate-400'}`}
                             >
                                <DynamicIcon name={section.icon} size={16} />
                                <span className="text-xs font-black uppercase tracking-tight truncate">{section.name}</span>
                             </button>
                           ))}
                        </div>
                     </div>
                     <div className="space-y-3">
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">Registry Items</label>
                        <div className="grid grid-cols-1 gap-2">
                           {currentSection?.items.map((item) => {
                              const hasData = item.elements.some(e => currentReport.answers[e.id]);
                              return (
                                <button 
                                  key={item.id} 
                                  onClick={() => { setActiveItemId(item.id); setMobileNavOpen(false); }}
                                  className={`w-full text-left p-4 rounded-2xl border transition-all flex items-center justify-between ${activeItemId === item.id ? 'bg-white border-2 border-[#606c38] text-[#606c38] shadow-md' : 'bg-slate-50 border-slate-100 text-slate-400'}`}
                                >
                                    <div className="flex items-center gap-3">
                                       <DynamicIcon name={item.icon} size={16} />
                                       <span className="text-xs font-bold uppercase truncate">{item.name}</span>
                                    </div>
                                    <div className={`w-1.5 h-1.5 rounded-full ${hasData ? 'bg-emerald-500' : 'bg-slate-200'}`} />
                                </button>
                              );
                           })}
                        </div>
                     </div>
                  </div>
                  <div className="p-6 border-t border-slate-100 bg-slate-50">
                    <button onClick={onClose} className="w-full py-4 bg-slate-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl">Complete Session</button>
                  </div>
               </div>
            </div>
         )}

         {/* Main Pane: Field Focus Area */}
         <main className="flex-1 overflow-y-auto bg-white no-scrollbar z-[70] h-full">
            {currentItem ? (
               <div className="w-full max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-16 space-y-10 sm:space-y-16 pb-40 animate-in fade-in duration-500">
                  <div className="space-y-4">
                     <div className="hidden sm:flex items-center gap-3 text-[#606c38] mb-2">
                        <DynamicIcon name={currentSection.icon} size={12} />
                        <span className="text-[10px] font-black uppercase tracking-[0.2em]">{currentSection.name}</span>
                        <ChevronRight size={12} className="text-slate-300" />
                        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Field Focus</span>
                     </div>
                     <div className="flex items-center gap-4 sm:gap-6">
                        <div className="p-3 sm:p-4 bg-slate-50 rounded-xl sm:rounded-2xl text-[#606c38] border border-slate-100 shadow-inner">
                           <DynamicIcon name={currentItem.icon} className="w-8 h-8 sm:w-10 sm:h-10" />
                        </div>
                        <h1 className="text-3xl sm:text-5xl font-black text-slate-900 tracking-tighter uppercase leading-none truncate">{currentItem.name}</h1>
                     </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 sm:gap-x-12 gap-y-8 sm:gap-y-10">
                     {currentItem.elements.map((el) => (
                       <div key={el.id} className="space-y-2 sm:space-y-3">
                          <label className="text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                             {el.label} {el.required && <span className="text-rose-500">*</span>}
                          </label>

                          {el.type === 'checkbox' && (
                             <button onClick={() => updateAnswer(el.id, !currentReport.answers[el.id])} className={`w-full p-4 rounded-xl border-2 transition-all flex items-center justify-between ${currentReport.answers[el.id] ? 'bg-emerald-50 border-emerald-500' : 'bg-slate-50 border-slate-100'}`}>
                                <div className="flex items-center gap-3">
                                   <div className={`w-6 h-6 rounded-lg flex items-center justify-center transition-colors ${currentReport.answers[el.id] ? 'bg-emerald-500 text-white' : 'bg-white border-2 border-slate-200'}`}>{currentReport.answers[el.id] && <Check size={14} />}</div>
                                   <span className={`text-xs font-black uppercase tracking-widest ${currentReport.answers[el.id] ? 'text-emerald-700' : 'text-slate-400'}`}>{currentReport.answers[el.id] ? 'Active' : 'N/A'}</span>
                                </div>
                             </button>
                          )}

                          {el.type === 'dropdown' && (
                             <select className="w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-xl font-bold text-sm text-slate-800 outline-none focus:bg-white focus:border-[#606c38] transition-all" value={currentReport.answers[el.id] || ''} onChange={(e) => updateAnswer(el.id, e.target.value)}>
                                <option value="">Select Value...</option>
                                {el.options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                             </select>
                          )}

                          {el.type === 'multiselect' && (
                             <div className="flex flex-wrap gap-2 p-3 bg-slate-50/50 rounded-xl border border-slate-100 min-h-[50px]">
                                {el.options?.map(opt => {
                                  const isSelected = (currentReport.answers[el.id] || []).includes(opt);
                                  return (
                                    <button key={opt} onClick={() => {
                                      const current = currentReport.answers[el.id] || [];
                                      const updated = isSelected ? current.filter((v: string) => v !== opt) : [...current, opt];
                                      updateAnswer(el.id, updated);
                                    }} className={`px-4 py-2 rounded-lg text-xs font-black uppercase transition-all ${isSelected ? 'bg-[#606c38] text-white shadow-md' : 'bg-white text-slate-400 border border-slate-200 hover:bg-slate-50'}`}>{opt}</button>
                                  );
                                })}
                             </div>
                          )}

                          {(el.type === 'text' || el.type === 'number') && (
                             <input 
                                type={el.type === 'number' ? 'number' : 'text'} 
                                className="w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-xl font-bold text-sm text-slate-700 focus:bg-white focus:border-[#606c38] outline-none transition-all" 
                                value={currentReport.answers[el.id] || ''} 
                                onChange={(e) => updateAnswer(el.id, e.target.value)} 
                             />
                          )}
                       </div>
                     ))}
                  </div>

                  <div className="pt-10 sm:pt-20 border-t border-slate-100 space-y-8 sm:space-y-10">
                     <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                           <h4 className="text-lg sm:text-xl font-black text-slate-900 uppercase tracking-tight flex items-center gap-3"><BookMarked size={24} className="text-blue-600" /> Observation Registry</h4>
                           <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Formal technical findings for this item</p>
                        </div>
                        <button onClick={() => setShowCatalog({ itemId: currentItem.id })} className="w-full sm:w-auto px-6 py-4 bg-slate-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center justify-center gap-3 shadow-xl"><Search size={16} /> Logic Catalog</button>
                     </div>

                     <div className="grid grid-cols-1 gap-6">
                        {(currentReport.answers.findings?.[currentItem.id] || []).map((finding: any) => {
                          const registryPhotos = hydrateImages(finding.registryImages || []);
                          const sessionPhotos = hydrateImages(finding.findingImages || []);

                          return (
                          <div key={finding.id} className="p-5 sm:p-8 bg-slate-50 rounded-2xl sm:rounded-xl border border-slate-100 group/finding relative animate-in fade-in slide-in-from-bottom-4 shadow-sm hover:shadow-md transition-all">
                             <div className="flex flex-col sm:flex-row items-start justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
                                <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                                   <div className="flex gap-1 shrink-0">
                                      {(finding.tagIds || []).map((tid: string) => {
                                         const tag = tags.find(t => t.id === tid);
                                         return tag ? <div key={tid} className="w-2.5 h-2.5 rounded-full shadow-sm" style={{ backgroundColor: tag.color }} title={tag.name} /> : null;
                                      })}
                                   </div>
                                   <h5 className="font-black text-slate-900 text-lg sm:text-xl uppercase tracking-tight truncate">{finding.title}</h5>
                                </div>
                                <div className="flex gap-2 w-full sm:w-auto">
                                   <button onClick={() => { setUploadingFindingPhoto(`${currentItem.id}:${finding.id}`); findingPhotoInputRef.current?.click(); }} className="flex-1 sm:flex-none p-3 bg-white rounded-xl text-blue-600 shadow-sm border border-slate-100 hover:bg-blue-50 transition-all flex items-center justify-center" title="Upload Photo"><Camera size={18} /></button>
                                   <button onClick={() => setEditingFinding({ itemId: currentItem.id, findingId: finding.id, title: finding.title, content: finding.content })} className="flex-1 sm:flex-none p-3 bg-white rounded-xl text-slate-600 shadow-sm border border-slate-100 hover:bg-slate-50 transition-all flex items-center justify-center"><Edit3 size={18} /></button>
                                   <button onClick={() => deleteFinding(currentItem.id, finding.id)} className="flex-1 sm:flex-none p-3 bg-white rounded-xl text-rose-500 shadow-sm border border-slate-100 hover:bg-rose-50 transition-all flex items-center justify-center"><Trash2 size={18} /></button>
                                </div>
                             </div>
                             
                             <div className="prose prose-slate prose-sm sm:prose-base max-w-none text-slate-700 font-medium leading-relaxed mb-8 sm:mb-10 pl-4 sm:pl-6 border-l-4 border-slate-200">
                               <ReactMarkdown remarkPlugins={[remarkGfm]}>{finding.content}</ReactMarkdown>
                             </div>

                             <div className="space-y-4">
                                {(sessionPhotos.length > 0 || registryPhotos.length > 0) ? (
                                   <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 sm:gap-8">
                                      {registryPhotos.map((img: any, i: number) => (
                                         <div key={`reg-${i}`} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col group/card transition-all hover:border-blue-500">
                                            <div className="relative aspect-video overflow-hidden cursor-pointer" onClick={() => setLightboxImage(img)}>
                                               <img src={img.url} className="w-full h-full object-cover transition-transform group-hover/card:scale-105 duration-700" />
                                               <div className="absolute inset-0 bg-slate-900/0 group-hover/card:bg-slate-900/10 transition-all flex items-center justify-center">
                                                  <Maximize2 size={24} className="text-white opacity-0 group-hover/card:opacity-100 scale-90 group-hover/card:scale-100 transition-all shadow-lg" />
                                               </div>
                                               <div className="absolute top-2 left-2 sm:top-3 sm:left-3 bg-blue-600 text-white px-2 sm:px-3 py-1 rounded-lg text-[8px] sm:text-[9px] font-black uppercase tracking-tighter shadow-lg">REGISTRY</div>
                                               <div className="absolute bottom-2 right-2 sm:bottom-3 sm:right-3 sm:opacity-0 group-hover/card:opacity-100 transition-all">
                                                  <button onClick={(e) => { e.stopPropagation(); setAnnotatingImage({ itemId: currentItem.id, findingId: finding.id, url: img.url, index: i, isRegistry: true }); }} className="p-2 bg-white text-blue-600 rounded-xl shadow-lg border border-slate-100"><Palette size={18} /></button>
                                               </div>
                                            </div>
                                            <div className="p-3 sm:p-4 flex gap-3 items-start bg-white">
                                               <AlignLeft size={14} className="text-slate-300 mt-1 shrink-0" />
                                               <textarea
                                                  placeholder="Technical context..."
                                                  className="w-full bg-transparent border-none outline-none resize-none text-[11px] font-bold text-slate-600 focus:ring-0 p-0 leading-relaxed italic"
                                                  rows={2}
                                                  value={img.description || ''}
                                                  onBlur={(e) => updateFindingImageDescription(currentItem.id, finding.id, i, e.target.value, true)}
                                                  onChange={(e) => handleUpdateDescriptionState(currentItem.id, finding.id, i, e.target.value, true)}
                                               />
                                            </div>
                                         </div>
                                      ))}
                                      {sessionPhotos.map((img: any, i: number) => (
                                         <div key={`ses-${i}`} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col group/card transition-all hover:border-emerald-500">
                                            <div className="relative aspect-video overflow-hidden cursor-pointer" onClick={() => setLightboxImage(img)}>
                                               <img src={img.url} className="w-full h-full object-cover transition-transform group-hover/card:scale-105 duration-700" />
                                               <div className="absolute inset-0 bg-slate-900/0 group-hover/card:bg-slate-900/10 transition-all flex items-center justify-center">
                                                  <Maximize2 size={24} className="text-white opacity-0 group-hover/card:opacity-100 scale-90 group-hover/card:scale-100 transition-all shadow-lg" />
                                               </div>
                                               <div className="absolute top-2 left-2 sm:top-3 sm:left-3 bg-emerald-600 text-white px-2 sm:px-3 py-1 rounded-lg text-[8px] sm:text-[9px] font-black uppercase tracking-tighter shadow-lg">SESSION</div>
                                               
                                               <div className="absolute bottom-2 inset-x-2 sm:bottom-3 sm:inset-x-3 flex justify-between items-center sm:opacity-0 group-hover/card:opacity-100 transition-all">
                                                  <div className="flex gap-1 bg-black/40 backdrop-blur-md p-1 sm:p-1.5 rounded-xl border border-white/20">
                                                     <button onClick={(e) => { e.stopPropagation(); moveFindingImage(currentItem.id, finding.id, i, 'left'); }} disabled={i === 0} className="p-1 sm:p-1.5 hover:bg-white/20 rounded-lg text-white disabled:opacity-20"><ArrowLeftCircle size={18} /></button>
                                                     <button onClick={(e) => { e.stopPropagation(); moveFindingImage(currentItem.id, finding.id, i, 'right'); }} disabled={i === sessionPhotos.length - 1} className="p-1 sm:p-1.5 hover:bg-white/20 rounded-lg text-white disabled:opacity-20"><ArrowRightCircle size={18} /></button>
                                                     <button onClick={(e) => { e.stopPropagation(); setAnnotatingImage({ itemId: currentItem.id, findingId: finding.id, url: img.url, index: i, isRegistry: false }); }} className="p-1 sm:p-1.5 hover:bg-white/20 rounded-lg text-white"><Palette size={16} /></button>
                                                  </div>
                                                  <button onClick={(e) => { e.stopPropagation(); deleteFindingImage(currentItem.id, finding.id, i); }} className="p-2 bg-rose-500 text-white rounded-xl shadow-lg hover:bg-rose-600 transition-colors border border-rose-400">
                                                     <Trash2 size={16} />
                                                  </button>
                                               </div>
                                            </div>
                                            <div className="p-3 sm:p-4 flex gap-3 items-start bg-white">
                                               <AlignLeft size={14} className="text-slate-300 mt-1 shrink-0" />
                                               <textarea
                                                  placeholder="Technical context..."
                                                  className="w-full bg-transparent border-none outline-none resize-none text-[11px] font-bold text-slate-600 focus:ring-0 p-0 leading-relaxed italic"
                                                  rows={2}
                                                  value={img.description || ''}
                                                  onBlur={(e) => updateFindingImageDescription(currentItem.id, finding.id, i, e.target.value, false)}
                                                  onChange={(e) => handleUpdateDescriptionState(currentItem.id, finding.id, i, e.target.value, false)}
                                               />
                                            </div>
                                         </div>
                                      ))}
                                   </div>
                                ) : (
                                   <p className="text-xs font-black text-slate-300 uppercase tracking-widest flex items-center gap-2"><ImageIcon size={14} /> No technical media artifacts</p>
                                )}
                             </div>
                          </div>
                        )})}
                        {(!currentReport.answers.findings?.[currentItem.id] || currentReport.answers.findings[currentItem.id].length === 0) && (
                          <div className="py-12 sm:py-20 text-center bg-slate-50/50 rounded-2xl sm:rounded-xl border-2 border-dashed border-slate-100">
                             <BookOpen size={40} className="mx-auto text-slate-200 mb-3" />
                             <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Technical log awaiting field observations</p>
                          </div>
                        )}
                     </div>
                  </div>
               </div>
            ) : null}

            <input ref={findingPhotoInputRef} type="file" accept="image/*" className="hidden" onChange={handleUploadFindingPhoto} />
         </main>
      </div>

      {showCatalog && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center">
           <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-lg" onClick={() => setShowCatalog(null)} />
           <div className="bg-white w-full h-full sm:h-auto sm:max-w-5xl sm:rounded-[2.5rem] shadow-2xl relative overflow-hidden flex flex-col max-h-[100vh] sm:max-h-[85vh] animate-in zoom-in-95 duration-300">
              <div className="p-6 sm:p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                 <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-600 text-white rounded-2xl shadow-xl shadow-blue-600/20"><BookOpen size={24} /></div>
                    <div>
                       <h3 className="font-black text-slate-900 text-sm sm:text-lg uppercase tracking-widest">Logic Library Studio</h3>
                       <p className="text-[9px] sm:text-xs text-slate-400 font-bold uppercase tracking-widest">Deploy technical field observations</p>
                    </div>
                 </div>
                 <button onClick={() => setShowCatalog(null)} className="p-3 hover:bg-white rounded-full text-slate-400 transition-colors"><X size={28} /></button>
              </div>
              <div className="p-6 sm:p-8 space-y-6 sm:space-y-8 flex-1 overflow-y-auto no-scrollbar">
                 <div className="relative">
                    <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400" size={24} />
                    <input 
                       className="w-full pl-16 pr-6 py-5 sm:py-6 bg-slate-50 border-2 border-slate-100 rounded-2xl text-base sm:text-lg font-bold outline-none focus:bg-white focus:border-blue-500 transition-all shadow-inner" 
                       placeholder="Search library..." 
                       value={narSearchQuery} 
                       onChange={(e) => setNarSearchQuery(e.target.value)} 
                       autoFocus
                    />
                 </div>
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 pb-20">
                    {narratives.filter(n => n.title.toLowerCase().includes(narSearchQuery.toLowerCase()) || (n.content || '').toLowerCase().includes(narSearchQuery.toLowerCase())).map(n => (
                      <div key={n.id} className="p-6 sm:p-10 bg-white border-2 border-slate-100 rounded-3xl hover:border-blue-500 hover:shadow-2xl transition-all group flex flex-col justify-between">
                         <div className="space-y-4 sm:space-y-6">
                            <div className="flex justify-between items-start gap-4">
                               <div className="flex flex-col gap-3 min-w-0">
                                  <div className="flex flex-wrap gap-2">
                                     {(n.tagIds || []).map(tid => {
                                        const tag = tags.find(t => t.id === tid);
                                        return tag ? <div key={tid} className="px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-tighter text-white shadow-sm" style={{ backgroundColor: tag.color }}>{tag.name}</div> : null;
                                     })}
                                  </div>
                                  <h5 className="font-black text-slate-900 text-lg sm:text-xl uppercase tracking-tight leading-tight">{n.title}</h5>
                               </div>
                               <button onClick={() => addFinding(showCatalog.itemId, n)} className="p-3 sm:p-4 bg-blue-50 text-blue-600 rounded-2xl hover:bg-blue-600 hover:text-white transition-all shadow-sm shrink-0"><Plus size={24} /></button>
                            </div>
                            <div className="prose prose-sm line-clamp-3 text-slate-500 italic font-medium leading-relaxed">
                               <ReactMarkdown remarkPlugins={[remarkGfm]}>{n.content}</ReactMarkdown>
                            </div>
                         </div>
                      </div>
                    ))}
                 </div>
              </div>
           </div>
        </div>
      )}

      {editingFinding && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center">
           <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-lg" onClick={() => setEditingFinding(null)} />
           <div className="bg-white w-full h-full sm:h-auto sm:max-w-3xl sm:rounded-[2.5rem] shadow-2xl relative overflow-hidden flex flex-col animate-in zoom-in-95">
              <div className="p-6 sm:p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                 <div className="flex items-center gap-4">
                    <div className="p-3 bg-[#606c38] text-white rounded-2xl shadow-xl"><Edit3 size={24} /></div>
                    <h3 className="font-black text-slate-900 text-sm sm:text-xl uppercase tracking-widest">Adjust Observation</h3>
                 </div>
                 <button onClick={() => setEditingFinding(null)} className="p-2 hover:bg-white rounded-full text-slate-400 transition-colors"><X size={28} /></button>
              </div>
              <div className="p-6 sm:p-10 space-y-6 sm:space-y-10 flex-1 overflow-y-auto">
                 <div className="space-y-3"><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Logic Headline</label><input className="w-full p-4 sm:p-5 bg-slate-50 border-2 border-slate-100 rounded-2xl text-base sm:text-lg font-bold text-slate-800 outline-none focus:bg-white focus:border-[#606c38] transition-all" value={editingFinding.title} onChange={(e) => setEditingFinding({ ...editingFinding, title: e.target.value })} /></div>
                 <div className="space-y-3"><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Technical Content</label><textarea rows={8} className="w-full p-4 sm:p-6 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm sm:text-lg font-medium text-slate-600 outline-none focus:bg-white focus:border-[#606c38] transition-all resize-none shadow-inner" value={editingFinding.content} onChange={(e) => setEditingFinding({ ...editingFinding, content: e.target.value })} /></div>
              </div>
              <div className="p-6 sm:p-8 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row justify-end gap-3 sm:gap-6">
                 <button onClick={() => setEditingFinding(null)} className="py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Cancel</button>
                 <button onClick={() => updateFinding(editingFinding.itemId, editingFinding.findingId, { title: editingFinding.title, content: editingFinding.content })} className="px-10 py-4 bg-slate-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-2xl active:scale-[0.98] transition-all">Apply Changes</button>
              </div>
           </div>
        </div>
      )}

      {/* Lightbox Overlay */}
      {lightboxImage && (
        <div className="fixed inset-0 z-[1200] bg-black/95 flex flex-col animate-in fade-in duration-300">
           <div className="flex justify-end p-4 sm:p-6">
              <button onClick={() => setLightboxImage(null)} className="p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all">
                 <X size={28} />
              </button>
           </div>
           <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-12 overflow-hidden" onClick={() => setLightboxImage(null)}>
              <img 
                 src={lightboxImage.url} 
                 className="max-w-full max-h-[75%] object-contain rounded-xl shadow-[0_0_80px_rgba(0,0,0,0.5)] animate-in zoom-in-95 duration-500" 
                 alt="Enlarged technical artifact"
                 onClick={(e) => e.stopPropagation()} 
              />
              {lightboxImage.description && (
                <div className="mt-6 sm:mt-8 max-w-2xl bg-black/40 backdrop-blur-md p-4 sm:p-6 rounded-[2rem] border border-white/10 animate-in slide-in-from-bottom-4 mx-4" onClick={(e) => e.stopPropagation()}>
                   <div className="flex gap-3 items-start">
                      <AlignLeft size={16} className="text-blue-400 mt-1 shrink-0" />
                      <p className="text-white text-xs sm:text-sm font-medium leading-relaxed italic">{lightboxImage.description}</p>
                   </div>
                </div>
              )}
           </div>
        </div>
      )}

      {/* Annotation Modal */}
      {annotatingImage && (
        <ImageAnnotator 
          imageUrl={annotatingImage.url} 
          onClose={() => setAnnotatingImage(null)}
          onSave={handleSaveAnnotatedImage}
        />
      )}
    </div>
  );
};

export default ReportEditor;